import React from "react";
import { FiAlertTriangle, FiChevronRight, FiGrid, FiList, FiRefreshCw, FiSearch } from "react-icons/fi";
import type { StorageFolder, Taxonomie } from "../../API";
import type { MediaTab, SortDir, SortKey, ViewMode } from "./types";
import { MEDIA_TABS, ROOT_PLACEHOLDER } from "./types";
import { TaxonomyFilter } from "./TaxonomyFilter";

export interface MediaToolbarProps {
	mode: "manage" | "pick";
	isRootView: boolean;
	tab: MediaTab;
	onTabChange: (tab: MediaTab) => void;
	breadcrumbs: StorageFolder[];
	currentPath: string;
	onNavigate: (path: string) => void;
	/** Valeur brute du champ de recherche : le débounce vit chez le parent. */
	searchValue: string;
	onSearchChange: (value: string) => void;
	taxonomies: Taxonomie[];
	taxonomyIds: string[];
	onTaxonomyChange: (ids: string[]) => void;
	sortKey: SortKey;
	sortDir: SortDir;
	onSortChange: (key: SortKey, dir: SortDir) => void;
	loading: boolean;
	onRefresh: () => void;
	viewMode: ViewMode;
	onViewModeChange: (mode: ViewMode) => void;
	/** Vue « orphelins » (mode manage uniquement). */
	orphansMode?: boolean;
	onToggleOrphans?: () => void;
	/** Onglets visibles (restriction `accept` de la Médiathèque) : MEDIA_TABS par défaut. */
	tabs?: typeof MEDIA_TABS;
}

const SORT_OPTIONS: Array<{ value: string; key: SortKey; dir: SortDir; label: string }> = [
	{ value: "date-desc", key: "date", dir: "desc", label: "Récents d'abord" },
	{ value: "date-asc", key: "date", dir: "asc", label: "Anciens d'abord" },
	{ value: "name-asc", key: "name", dir: "asc", label: "Nom A → Z" },
	{ value: "name-desc", key: "name", dir: "desc", label: "Nom Z → A" },
	{ value: "size-desc", key: "size", dir: "desc", label: "Plus lourds d'abord" },
	{ value: "size-asc", key: "size", dir: "asc", label: "Plus légers d'abord" }
];

/**
 * Barre d'outils de la Médiathèque : onglets (racine) ou fil d'ariane (dossier) à gauche ;
 * recherche, filtre taxonomies, tri, vue orphelins, rafraîchir et bascule grille/liste à droite.
 * Composant sans état (hors UI du filtre taxonomies) : tout remonte au parent en callbacks.
 */
export const MediaToolbar: React.FC<MediaToolbarProps> = ({
	mode,
	isRootView,
	tab,
	onTabChange,
	breadcrumbs,
	currentPath,
	onNavigate,
	searchValue,
	onSearchChange,
	taxonomies,
	taxonomyIds,
	onTaxonomyChange,
	sortKey,
	sortDir,
	onSortChange,
	loading,
	onRefresh,
	viewMode,
	onViewModeChange,
	orphansMode = false,
	onToggleOrphans,
	tabs = MEDIA_TABS
}) => {
	const sortValue = SORT_OPTIONS.find((option) => option.key === sortKey && option.dir === sortDir)?.value ?? "date-desc";
	const crumbs = breadcrumbs.length ? breadcrumbs : [{ id: currentPath, name: currentPath, path: currentPath }];

	return (
		<div className="flex flex-wrap items-center gap-2">
			{isRootView ? (
				tabs.length > 1 && (
				<div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5 shadow-sm" role="tablist" aria-label="Filtrer par type de média">
					{tabs.map((item) => (
						<button
							key={item.id}
							type="button"
							role="tab"
							aria-selected={tab === item.id}
							onClick={() => onTabChange(item.id)}
							className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
								tab === item.id ? "bg-[#005CA9] text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
							}`}
						>
							{item.label}
						</button>
					))}
				</div>
				)
			) : (
				<nav aria-label="Fil d'ariane" className="flex min-w-0 flex-wrap items-center gap-1 text-sm">
					<button
						type="button"
						onClick={() => onNavigate(ROOT_PLACEHOLDER)}
						className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-200"
					>
						Tous les médias
					</button>
					{crumbs.map((crumb, index, list) => {
						const crumbPath = crumb.path ?? "";
						const isLast = index === list.length - 1;
						return (
							<React.Fragment key={crumb.id ?? crumbPath ?? `${crumb.name}-${index}`}>
								<FiChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" aria-hidden />
								<button
									type="button"
									onClick={() => crumbPath && onNavigate(crumbPath)}
									disabled={isLast || !crumbPath}
									className={`max-w-[16rem] truncate rounded-full px-3 py-1 text-xs font-semibold transition ${
										isLast ? "bg-slate-900 text-white" : crumbPath ? "bg-slate-100 text-slate-600 hover:bg-slate-200" : "bg-slate-100 text-slate-400"
									} disabled:cursor-default`}
								>
									{crumb.name ?? crumbPath ?? "Dossier"}
								</button>
							</React.Fragment>
						);
					})}
				</nav>
			)}

			<div className="ml-auto flex flex-wrap items-center gap-2">
				<div className="relative">
					<FiSearch className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" aria-hidden />
					<input
						type="search"
						value={searchValue}
						onChange={(e) => onSearchChange(e.target.value)}
						placeholder={isRootView ? "Rechercher dans tous les médias…" : "Rechercher dans ce dossier…"}
						aria-label="Rechercher un fichier"
						className="w-56 rounded-full border border-slate-200 bg-slate-50 py-1.5 pl-9 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-slate-400 focus:bg-white focus:outline-none xl:w-64"
					/>
				</div>
				<TaxonomyFilter taxonomies={taxonomies} value={taxonomyIds} onChange={onTaxonomyChange} />
				<label className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white py-0.5 pl-3 pr-1.5 text-xs font-semibold text-slate-500 shadow-sm">
					Tri
					<select
						value={sortValue}
						onChange={(e) => {
							const option = SORT_OPTIONS.find((item) => item.value === e.target.value);
							if (option) onSortChange(option.key, option.dir);
						}}
						aria-label="Trier les fichiers"
						className="rounded-full bg-transparent py-1 pr-1 text-xs font-semibold text-slate-700 focus:outline-none"
					>
						{SORT_OPTIONS.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</label>
				{mode === "manage" && onToggleOrphans && (
					<button
						type="button"
						onClick={onToggleOrphans}
						aria-pressed={orphansMode}
						title="Fichiers qu'aucun contenu ne référence"
						className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold shadow-sm transition ${
							orphansMode
								? "border-amber-400 bg-amber-50 text-amber-700"
								: "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700"
						}`}
					>
						<FiAlertTriangle className="h-3.5 w-3.5" aria-hidden /> Orphelins
					</button>
				)}
				<button
					type="button"
					onClick={onRefresh}
					disabled={loading}
					title="Rafraîchir"
					aria-label="Rafraîchir"
					className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
				>
					<FiRefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
				</button>
				<div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5 shadow-sm" role="group" aria-label="Mode d'affichage">
					<button
						type="button"
						onClick={() => onViewModeChange("grid")}
						aria-pressed={viewMode === "grid"}
						title="Vue grille"
						className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
							viewMode === "grid" ? "bg-slate-900 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
						}`}
					>
						<FiGrid className="h-3.5 w-3.5" /> Grille
					</button>
					<button
						type="button"
						onClick={() => onViewModeChange("list")}
						aria-pressed={viewMode === "list"}
						title="Vue liste"
						className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
							viewMode === "list" ? "bg-slate-900 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
						}`}
					>
						<FiList className="h-3.5 w-3.5" /> Liste
					</button>
				</div>
			</div>
		</div>
	);
};
